import { useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * RegulatoryAdvisorySignup — compact opt-in form.
 *
 * Sends the visitor's email to /api/subscribe with source
 * "regulatory_advisories". The backend mints a confirm token, sends a
 * double-opt-in email via Resend, and only counts confirmed emails
 * toward the advisory-blast recipient list.
 *
 * Renders in two visual modes:
 *   variant="bar"   — single-row band with input + button (used inside
 *                     articles and mid-page CTAs)
 *   variant="card"  — bordered card with kicker + headline + form (used
 *                     as a standalone opt-in block)
 */
export default function RegulatoryAdvisorySignup({
  variant = "bar",
  kicker = "Regulatory Advisories",
  headline = "Receive future federal and state advisories from the firm.",
  subhead = "Occasional, editorial, and never sold.",
}) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setStatus("loading");
    try {
      await axios.post(`${API}/api/subscribe`, {
        email,
        source: "regulatory_advisories",
      });
      setStatus("success");
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
      setStatus("error");
    }
  };

  if (variant === "card") {
    return (
      <section
        className="bg-linen border border-brass/40 p-8 md:p-12"
        data-testid="advisory-signup-card"
      >
        <div className="kicker">{kicker}</div>
        <h3 className="mt-4 font-display text-3xl md:text-4xl leading-[1.05]">
          {headline}
        </h3>
        <p className="mt-4 text-slate2 font-cormorant italic text-lg leading-relaxed">
          {subhead}
        </p>
        {status === "success" ? (
          <div className="mt-8 font-cormorant italic text-xl text-oxblood" data-testid="advisory-signup-success">
            &mdash; Please check your inbox to confirm.
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-3" data-testid="advisory-signup-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@firm.com"
              className="field flex-1"
              data-testid="advisory-signup-email"
            />
            <button
              type="submit"
              disabled={status === "loading"}
              className="btn-brass disabled:opacity-40 whitespace-nowrap"
              data-testid="advisory-signup-submit"
            >
              {status === "loading" ? "Subscribing…" : "Subscribe"}
            </button>
          </form>
        )}
        {error && (
          <div className="mt-4 text-oxblood font-serif italic text-sm" data-testid="advisory-signup-error">
            &mdash; {error}
          </div>
        )}
      </section>
    );
  }

  // variant = "bar"
  return (
    <div className="hairline-top hairline-bottom py-8" data-testid="advisory-signup-bar">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
        <div className="md:col-span-5">
          <div className="kicker">{kicker}</div>
          <p className="mt-2 font-cormorant italic text-lg text-navy leading-snug">
            {headline}
          </p>
        </div>
        {status === "success" ? (
          <div
            className="md:col-span-7 font-cormorant italic text-lg text-oxblood"
            data-testid="advisory-signup-success"
          >
            &mdash; Thank you. Please confirm from your inbox.
          </div>
        ) : (
          <form onSubmit={submit} className="md:col-span-7 flex flex-col sm:flex-row gap-3" data-testid="advisory-signup-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@firm.com"
              className="field flex-1"
              data-testid="advisory-signup-email"
            />
            <button
              type="submit"
              disabled={status === "loading"}
              className="btn-brass disabled:opacity-40 whitespace-nowrap"
              data-testid="advisory-signup-submit"
            >
              {status === "loading" ? "Subscribing…" : "Subscribe"}
            </button>
          </form>
        )}
      </div>
      {error && (
        <div className="mt-3 text-oxblood font-serif italic text-sm" data-testid="advisory-signup-error">
          &mdash; {error}
        </div>
      )}
    </div>
  );
}
